// This file is generated automatically by gen_user_events.js script.
// Do not change this file manually.

import type {
    UserEventRelatedResourceView,
    EventSource,
    SimpleUserForUserEvent,
    TagForUserEvent,
    UserGroupUsersForUserEvent,
    AssigneeForUserEvent,
    ControlAssigneesForUserEvent,
    ControlNotificationRecipientsForUserEvent,
    CalendarForUserEvent,
    ControlCoveredPeriodForUserEvent,
    NotificationOffsetForUserEvent,
    RecurrenceForUserEvent,
    ReviewerOffsetsForUserEvent,
    ControlProgramAdminsForUserEvent,
    ControlProgramEditorsForUserEvent,
    ControlProgramViewersForUserEvent,
    SimpleEntityForUserEvent,
    RiskDirectoryAdminsForUserEvent,
    RiskDirectoryEditorsForUserEvent,
    RiskDirectoryViewersForUserEvent,
    EntityAdminsForUserEvent,
    EntityEditorsForUserEvent,
    EntityViewersForUserEvent,
    ControlAccessRightsForAdminUsers,
    ControlAccessRightsForEditUsers,
    ControlAccessRightsForViewUsers,
    EntityDescriptionForUserEvent,
    EntityForUserEvent,
    EntityNameForUserEvent,
    EntityResponsibleForUserEvent,
    EntityTagsForUserEvent,
    RiskAccessRightsForAdminUsers,
    RiskAccessRightsForEditUsers,
    RiskAccessRightsForViewUsers,
    EventMaybeAuthPhoneNumber,
    UserCultureForUserEvent,
    UserEmailForUserEvent,
    UserEmailSettingsForUserEvent,
    UserFirstNameForUserEvent,
    UserPasswordAuthenticationForUserEvent,
    UserRolesForUserEvent,
    UserTimezoneForUserEvent,
    ApiKeyForUserEvent,
    UserForUserEvent,
    ControlPolicyForUserEvent,
    TestingProgramAdminsForUserEvent,
    TestingProgramEditorsForUserEvent,
    TestingProgramViewersForUserEvent,
} from "api/generated/model";

const ResourceType = {
    USER: "User" as const,
    USER_GROUP: "UserGroup" as const,
    CONTROL_PROGRAM: "ControlProgram" as const,
    CONTROL: "Control" as const,
    RISK_DIRECTORY: "RiskDirectory" as const,
    ENTITY: "Entity" as const,
    PUSH_REPORT: "PushReport" as const,
    API_KEY: "ApiKey" as const,
    TESTING_PROGRAM: "TestingProgram" as const,
    TESTING_CONTROL: "TestingControl" as const,
    SCIM_USER: "ScimUser" as const,
    ORGANISATION_SETTINGS: "OrganisationSettings" as const,
};

const RESOURCE_TYPES = [
    ResourceType.USER,
    ResourceType.USER_GROUP,
    ResourceType.CONTROL_PROGRAM,
    ResourceType.CONTROL,
    ResourceType.RISK_DIRECTORY,
    ResourceType.ENTITY,
    ResourceType.PUSH_REPORT,
    ResourceType.API_KEY,
    ResourceType.TESTING_PROGRAM,
    ResourceType.TESTING_CONTROL,
    ResourceType.SCIM_USER,
];

const EventName = {
    CREATED: "Created" as const,
    CHANGED: "Changed" as const,
    DELETED: "Deleted" as const,
    DISABLED: "Disabled" as const,
    ENABLED: "Enabled" as const,

    CULTURE_CHANGED: "CultureChanged" as const,
    EMAIL_CHANGED: "EmailChanged" as const,
    EMAIL_SETTINGS_CHANGED: "EmailSettingsChanged" as const,
    NOTIFICATION_PREFERENCE_CHANGED: "NotificationPreferenceChanged" as const,
    FIRST_NAME_CHANGED: "FirstNameChanged" as const,
    LAST_NAME_CHANGED: "LastNameChanged" as const,
    INVITED: "Invited" as const,
    LOGGED_IN: "LoggedIn" as const,
    LOGGED_OUT: "LoggedOut" as const,
    PASSWORD_AUTHENTICATION_CHANGED: "PasswordAuthenticationChanged" as const,
    PASSWORD_CHANGED: "PasswordChanged" as const,
    PASSWORD_RESET: "PasswordReset" as const,
    PASSWORD_RESET_REQUESTED: "PasswordResetRequested" as const,
    PHONE_NUMBER_CHANGED: "PhoneNumberChanged" as const,
    PHONE_NUMBER_VERIFIED: "PhoneNumberVerified" as const,
    REGISTERED: "Registered" as const,
    ROLES_CHANGED: "RolesChanged" as const,
    TIMEZONE_CHANGED: "TimezoneChanged" as const,

    USERS_CHANGED: "UsersChanged" as const,

    DESCRIPTION_CHANGED: "DescriptionChanged" as const,
    NAME_CHANGED: "NameChanged" as const,
    TITLE_CHANGED: "TitleChanged" as const,

    ASSIGNEES_CHANGED: "AssigneesChanged" as const,
    CONTROL_PROGRAM_CHANGED: "ControlProgramChanged" as const,
    LOGIN_PROTECTION_CHANGED: "LoginProtectionChanged" as const,
    RECURRENCE_CHANGED: "RecurrenceChanged" as const,
    SEGREGATION_OF_DUTIES_CHANGED: "SegregationOfDutiesChanged" as const,
    TAGS_CHANGED: "TagsChanged" as const,
    TASKS_CHANGED: "TasksChanged" as const,

    STATUS_CHANGED: "StatusChanged" as const,
    RESPONSIBLE_CHANGED: "ResponsibleChanged" as const,
    ADMINS_CHANGED: "AdminsChanged" as const,
    EDITORS_CHANGED: "EditorsChanged" as const,
    VIEWERS_CHANGED: "ViewersChanged" as const,
    CONTROL_ADMINS_CHANGED: "ControlAdminsChanged" as const,
    CONTROL_EDITORS_CHANGED: "ControlEditorsChanged" as const,
    CONTROL_VIEWERS_CHANGED: "ControlViewersChanged" as const,
    RISK_ADMINS_CHANGED: "RiskAdminsChanged" as const,
    RISK_EDITORS_CHANGED: "RiskEditorsChanged" as const,
    RISK_VIEWERS_CHANGED: "RiskViewersChanged" as const,
    ENTITIES_CHANGED: "EntitiesChanged" as const,

    SAMPLE_TASKS_CHANGED: "SampleTasksChanged" as const,
    SAMPLING_CRITERIA_CHANGED: "SamplingCriteriaChanged" as const,
    SCHEDULING_CHANGED: "SchedulingChanged" as const,
    RANDOM_SAMPLE_COUNT_CHANGED: "RandomSampleCountChanged" as const,

    NOTIFICATION_RECIPIENTS_CHANGED: "NotificationRecipientsChanged" as const,

    CONTROL_POLICY_CHANGED: "ControlPolicyChanged" as const,
};

interface EventWithRelations {
    event: UserEvent;
    relations: Array<UserEventRelatedResourceView>;
}

interface CreatedUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CREATED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: null | undefined;
    newValue: UserForUserEvent | null | undefined;
    source: EventSource | null;
}

interface CultureChangedUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CULTURE_CHANGED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: UserCultureForUserEvent;
    newValue: UserCultureForUserEvent;
    source: EventSource | null;
}

interface DisabledUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.DISABLED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: UserForUserEvent | null | undefined;
    newValue: null | undefined;
    source: EventSource | null;
}

interface EmailChangedUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.EMAIL_CHANGED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: UserEmailForUserEvent;
    newValue: UserEmailForUserEvent;
    source: EventSource | null;
}

interface EmailSettingsChangedUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.EMAIL_SETTINGS_CHANGED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: UserEmailSettingsForUserEvent;
    newValue: UserEmailSettingsForUserEvent;
    source: EventSource | null;
}

interface NotificationPreferenceForUserEvent {
    assignmentNotifications: boolean;
    deadlineReminders: boolean;
    reviewRequests: boolean;
    reviewReminders: boolean;
    testingAssignmentsReminders: boolean;
    reportDeliveries: boolean;
    approvalConfirmations: boolean;
    watchedControlCompletions: boolean;
}

interface NotificationPreferenceChangedUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.NOTIFICATION_PREFERENCE_CHANGED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: NotificationPreferenceForUserEvent;
    newValue: NotificationPreferenceForUserEvent;
    source: EventSource | null;
}

interface EnabledUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.ENABLED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: null | undefined;
    newValue: UserForUserEvent | null | undefined;
    source: EventSource | null;
}

interface FirstNameChangedUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.FIRST_NAME_CHANGED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: UserFirstNameForUserEvent;
    newValue: UserFirstNameForUserEvent;
    source: EventSource | null;
}

interface InvitedUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.INVITED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: null | undefined;
    newValue: null | undefined;
    source: EventSource | null;
}

interface UserLastNameForUserEvent {
    lastname: string;
}

interface LastNameChangedUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.LAST_NAME_CHANGED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: UserLastNameForUserEvent;
    newValue: UserLastNameForUserEvent;
    source: EventSource | null;
}

interface LoggedInUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.LOGGED_IN;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: null | undefined;
    newValue: null | undefined;
    source: EventSource | null;
}

interface LoggedOutUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.LOGGED_OUT;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: null | undefined;
    newValue: null | undefined;
    source: EventSource | null;
}

interface PasswordAuthenticationChangedUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.PASSWORD_AUTHENTICATION_CHANGED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: UserPasswordAuthenticationForUserEvent;
    newValue: UserPasswordAuthenticationForUserEvent;
    source: EventSource | null;
}

interface PasswordChangedUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.PASSWORD_CHANGED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: null | undefined;
    newValue: null | undefined;
    source: EventSource | null;
}

interface PasswordResetUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.PASSWORD_RESET;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: null | undefined;
    newValue: null | undefined;
    source: EventSource | null;
}

interface PasswordResetRequestedUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.PASSWORD_RESET_REQUESTED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: null | undefined;
    newValue: null | undefined;
    source: EventSource | null;
}

interface PhoneNumberChangedUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.PHONE_NUMBER_CHANGED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: EventMaybeAuthPhoneNumber;
    newValue: EventMaybeAuthPhoneNumber;
    source: EventSource | null;
}

interface PhoneNumberVerifiedUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.PHONE_NUMBER_VERIFIED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: null | undefined;
    newValue: null | undefined;
    source: EventSource | null;
}

interface RegisteredUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.REGISTERED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: null | undefined;
    newValue: null | undefined;
    source: EventSource | null;
}

interface RolesChangedUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.ROLES_CHANGED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: UserRolesForUserEvent;
    newValue: UserRolesForUserEvent;
    source: EventSource | null;
}

interface TimezoneChangedUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.TIMEZONE_CHANGED;
    resourceType: typeof ResourceType.USER;
    resourceId: string;
    resourceName: string;
    oldValue: UserTimezoneForUserEvent;
    newValue: UserTimezoneForUserEvent;
    source: EventSource | null;
}

interface UsersChangedUserGroupUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.USERS_CHANGED;
    resourceType: typeof ResourceType.USER_GROUP;
    resourceId: string;
    resourceName: string;
    oldValue: UserGroupUsersForUserEvent;
    newValue: UserGroupUsersForUserEvent;
    source: EventSource | null;
}

interface DeletedUserGroupUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.DELETED;
    resourceType: typeof ResourceType.USER_GROUP;
    resourceId: string;
    resourceName: string;
    oldValue: UserGroupUserEvent;
    newValue: null | undefined;
    source: EventSource | null;
}

interface CreatedUserGroupUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CREATED;
    resourceType: typeof ResourceType.USER_GROUP;
    resourceId: string;
    resourceName: string;
    oldValue: null | undefined;
    newValue: UserGroupUserEvent;
    source: EventSource | null;
}

interface NameChangedUserGroupUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.NAME_CHANGED;
    resourceType: typeof ResourceType.USER_GROUP;
    resourceId: string;
    resourceName: string;
    oldValue: UserGroupUserEvent;
    newValue: UserGroupUserEvent;
    source: EventSource | null;
}

interface DescriptionChangedUserGroupUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.DESCRIPTION_CHANGED;
    resourceType: typeof ResourceType.USER_GROUP;
    resourceId: string;
    resourceName: string;
    oldValue: UserGroupUserEvent;
    newValue: UserGroupUserEvent;
    source: EventSource | null;
}

interface EditorsChangedUserGroupUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.EDITORS_CHANGED;
    resourceType: typeof ResourceType.USER_GROUP;
    resourceId: string;
    resourceName: string;
    oldValue: UserGroupUserEvent;
    newValue: UserGroupUserEvent;
    source: EventSource | null;
}

interface AdminsChangedUserGroupUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.ADMINS_CHANGED;
    resourceType: typeof ResourceType.USER_GROUP;
    resourceId: string;
    resourceName: string;
    oldValue: UserGroupUserEvent;
    newValue: UserGroupUserEvent;
    source: EventSource | null;
}

interface ControlForUserEvent {
    id: string;
    title: string;
    description: string;
    assignees: Array<AssigneeForUserEvent>;
    // TODO definetype
    tasks: Array<unknown>;
    // recurrence: RecurrenceForUserEvent (flattened)
    // gives the following fields -----
    recurrenceRule: RecurrenceForUserEvent;
    notificationOffset: NotificationOffsetForUserEvent;
    reviewerOffsets: ReviewerOffsetsForUserEvent;
    coveredPeriod?: ControlCoveredPeriodForUserEvent;
    calendar?: CalendarForUserEvent;
    financialYearId?: number;
    reminders: Array<number>;
    // ------ end of recurrence
    controlProgram: SimpleControlProgramForUserEvent;
    loginProtectionEnabled: boolean;
    segregationOfDutiesEnabled: boolean;
    tags: Array<TagForUserEvent>;
    lastUpdated?: string;
    lastUpdatedBy?: SimpleUserForUserEvent;
}

interface CreatedControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CREATED;
    resourceType: typeof ResourceType.CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: null | undefined;
    newValue: ControlForUserEvent;
    source: EventSource | null;
}

interface ControlControlProgramForUserEvent {
    controlProgram: SimpleControlProgramForUserEvent;
}

interface ControlProgramChangedControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CONTROL_PROGRAM_CHANGED;
    resourceType: typeof ResourceType.CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: ControlControlProgramForUserEvent;
    newValue: ControlControlProgramForUserEvent;
    source: EventSource | null;
}

interface ControlTitleForUserEvent {
    title: string;
}

interface TitleChangedControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.TITLE_CHANGED;
    resourceType: typeof ResourceType.CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: ControlTitleForUserEvent;
    newValue: ControlTitleForUserEvent;
    source: EventSource | null;
}

interface ControlDescriptionForUserEvent {
    description: string;
}

interface DescriptionChangedControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.DESCRIPTION_CHANGED;
    resourceType: typeof ResourceType.CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: ControlDescriptionForUserEvent;
    newValue: ControlDescriptionForUserEvent;
    source: EventSource | null;
}

interface ControlTagsForUserEvent {
    tags: Array<TagForUserEvent>;
}

interface TagsChangedControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.TAGS_CHANGED;
    resourceType: typeof ResourceType.CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: ControlTagsForUserEvent;
    newValue: ControlTagsForUserEvent;
    source: EventSource | null;
}

interface ControlRecurrenceForUserEvent {
    recurrenceRule: RecurrenceForUserEvent;
    notificationOffset: NotificationOffsetForUserEvent;
    reviewerOffsets: ReviewerOffsetsForUserEvent;
    coveredPeriod?: ControlCoveredPeriodForUserEvent;
    calendar?: CalendarForUserEvent;
    financialYearId?: number;
    reminders: Array<number>;
}

interface RecurrenceChangedControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.RECURRENCE_CHANGED;
    resourceType: typeof ResourceType.CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: ControlRecurrenceForUserEvent;
    newValue: ControlRecurrenceForUserEvent;
    source: EventSource | null;
}

interface AssigneesChangedControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.ASSIGNEES_CHANGED;
    resourceType: typeof ResourceType.CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: ControlAssigneesForUserEvent;
    newValue: ControlAssigneesForUserEvent;
    source: EventSource | null;
}

interface ControlLoginProtectionForUserEvent {
    loginProtectionEnabled: boolean;
}

interface LoginProtectionChangedControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.LOGIN_PROTECTION_CHANGED;
    resourceType: typeof ResourceType.CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: ControlLoginProtectionForUserEvent;
    newValue: ControlLoginProtectionForUserEvent;
    source: EventSource | null;
}

interface ControlSegregationOfDutiesForUserEvent {
    segregationOfDutiesEnabled: boolean;
}

interface SegregationOfDutiesChangedControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.SEGREGATION_OF_DUTIES_CHANGED;
    resourceType: typeof ResourceType.CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: ControlSegregationOfDutiesForUserEvent;
    newValue: ControlSegregationOfDutiesForUserEvent;
    source: EventSource | null;
}

interface ControlTasksForUserEvent {
    // TODO definetype
    tasks: Array<unknown>;
}

interface TasksChangedControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.TASKS_CHANGED;
    resourceType: typeof ResourceType.CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: ControlTasksForUserEvent;
    newValue: ControlTasksForUserEvent;
    source: EventSource | null;
}

interface DeletedControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.DELETED;
    resourceType: typeof ResourceType.CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: ControlForUserEvent;
    newValue: null | undefined;
    source: EventSource | null;
}

interface NotificationRecipientsChangedControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.NOTIFICATION_RECIPIENTS_CHANGED;
    resourceType: typeof ResourceType.CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: ControlNotificationRecipientsForUserEvent;
    newValue: ControlNotificationRecipientsForUserEvent;
    source: EventSource | null;
}

interface ControlProgramForUserEvent {
    id: number;
    name: string;
    description: string;
    created: string;
    createdBy: SimpleUserForUserEvent;
    responsible: SimpleUserForUserEvent | null | undefined;
    running: boolean;
    tags: Array<TagForUserEvent>;
    usersWithAdminAccess: Array<SimpleUserForUserEvent> | null | undefined;
    usersWithEditAccess: Array<SimpleUserForUserEvent>;
    usersWithViewAccess: Array<SimpleUserForUserEvent>;
    entities: Array<SimpleEntityForUserEvent>;
}

interface SimpleControlProgramForUserEvent {
    id: number;
    name: string;
}

interface CreatedControlProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CREATED;
    resourceType: typeof ResourceType.CONTROL_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: null | undefined;
    newValue: ControlProgramForUserEvent;
    source: EventSource | null;
}

interface ControlProgramNameForUserEvent {
    name: string;
}

interface NameChangedControlProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.NAME_CHANGED;
    resourceType: typeof ResourceType.CONTROL_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: ControlProgramNameForUserEvent;
    newValue: ControlProgramNameForUserEvent;
    source: EventSource | null;
}

interface ControlProgramDescriptionForUserEvent {
    description: string;
}

interface DescriptionChangedControlProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.DESCRIPTION_CHANGED;
    resourceType: typeof ResourceType.CONTROL_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: ControlProgramDescriptionForUserEvent;
    newValue: ControlProgramDescriptionForUserEvent;
    source: EventSource | null;
}

interface ControlProgramStatusForUserEvent {
    running: boolean;
}

interface StatusChangedControlProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.STATUS_CHANGED;
    resourceType: typeof ResourceType.CONTROL_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: ControlProgramStatusForUserEvent;
    newValue: ControlProgramStatusForUserEvent;
    source: EventSource | null;
}

interface ControlProgramTagsForUserEvent {
    tags: Array<TagForUserEvent>;
}

interface TagsChangedControlProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.TAGS_CHANGED;
    resourceType: typeof ResourceType.CONTROL_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: ControlProgramTagsForUserEvent;
    newValue: ControlProgramTagsForUserEvent;
    source: EventSource | null;
}

interface ControlProgramResponsibleForUserEvent {
    responsible: SimpleUserForUserEvent | null | undefined;
}

interface ResponsibleChangedControlProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.RESPONSIBLE_CHANGED;
    resourceType: typeof ResourceType.CONTROL_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: ControlProgramResponsibleForUserEvent;
    newValue: ControlProgramResponsibleForUserEvent;
    source: EventSource | null;
}

interface AdminsChangedControlProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.ADMINS_CHANGED;
    resourceType: typeof ResourceType.CONTROL_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: ControlProgramAdminsForUserEvent;
    newValue: ControlProgramAdminsForUserEvent;
    source: EventSource | null;
}

interface EditorsChangedControlProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.EDITORS_CHANGED;
    resourceType: typeof ResourceType.CONTROL_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: ControlProgramEditorsForUserEvent;
    newValue: ControlProgramEditorsForUserEvent;
    source: EventSource | null;
}

interface ViewersChangedControlProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.VIEWERS_CHANGED;
    resourceType: typeof ResourceType.CONTROL_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: ControlProgramViewersForUserEvent;
    newValue: ControlProgramViewersForUserEvent;
    source: EventSource | null;
}

interface ControlProgramEntitiesForUserEvent {
    entities: Array<SimpleEntityForUserEvent>;
}

interface EntitiesChangedControlProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.ENTITIES_CHANGED;
    resourceType: typeof ResourceType.CONTROL_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: ControlProgramEntitiesForUserEvent;
    newValue: ControlProgramEntitiesForUserEvent;
    source: EventSource | null;
}

interface DeletedControlProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.DELETED;
    resourceType: typeof ResourceType.CONTROL_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: ControlProgramForUserEvent;
    newValue: null | undefined;
    source: EventSource | null;
}

interface AdminsChangedRiskDirectoryUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.ADMINS_CHANGED;
    resourceType: typeof ResourceType.RISK_DIRECTORY;
    resourceId: string;
    resourceName: string;
    oldValue: RiskDirectoryAdminsForUserEvent;
    newValue: RiskDirectoryAdminsForUserEvent;
    source: EventSource | null;
}

interface EditorsChangedRiskDirectoryUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.EDITORS_CHANGED;
    resourceType: typeof ResourceType.RISK_DIRECTORY;
    resourceId: string;
    resourceName: string;
    oldValue: RiskDirectoryEditorsForUserEvent;
    newValue: RiskDirectoryEditorsForUserEvent;
    source: EventSource | null;
}

interface ViewersChangedRiskDirectoryUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.VIEWERS_CHANGED;
    resourceType: typeof ResourceType.RISK_DIRECTORY;
    resourceId: string;
    resourceName: string;
    oldValue: RiskDirectoryViewersForUserEvent;
    newValue: RiskDirectoryViewersForUserEvent;
    source: EventSource | null;
}

interface CreatedEntityUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CREATED;
    resourceType: typeof ResourceType.ENTITY;
    resourceId: string;
    resourceName: string;
    oldValue: null | undefined;
    newValue: EntityForUserEvent;
    source: EventSource | null;
}

interface DeletedEntityUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.DELETED;
    resourceType: typeof ResourceType.ENTITY;
    resourceId: string;
    resourceName: string;
    oldValue: EntityForUserEvent;
    newValue: null | undefined;
    source: EventSource | null;
}

interface NameChangedEntityUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.NAME_CHANGED;
    resourceType: typeof ResourceType.ENTITY;
    resourceId: string;
    resourceName: string;
    oldValue: EntityNameForUserEvent;
    newValue: EntityNameForUserEvent;
    source: EventSource | null;
}

interface DescriptionChangedEntityUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.DESCRIPTION_CHANGED;
    resourceType: typeof ResourceType.ENTITY;
    resourceId: string;
    resourceName: string;
    oldValue: EntityDescriptionForUserEvent;
    newValue: EntityDescriptionForUserEvent;
    source: EventSource | null;
}

interface ResponsibleChangedEntityUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.RESPONSIBLE_CHANGED;
    resourceType: typeof ResourceType.ENTITY;
    resourceId: string;
    resourceName: string;
    oldValue: EntityResponsibleForUserEvent;
    newValue: EntityResponsibleForUserEvent;
    source: EventSource | null;
}

interface AdminsChangedEntityUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.ADMINS_CHANGED;
    resourceType: typeof ResourceType.ENTITY;
    resourceId: string;
    resourceName: string;
    oldValue: EntityAdminsForUserEvent;
    newValue: EntityAdminsForUserEvent;
    source: EventSource | null;
}

interface EditorsChangedEntityUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.EDITORS_CHANGED;
    resourceType: typeof ResourceType.ENTITY;
    resourceId: string;
    resourceName: string;
    oldValue: EntityEditorsForUserEvent;
    newValue: EntityEditorsForUserEvent;
    source: EventSource | null;
}

interface ViewersChangedEntityUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.VIEWERS_CHANGED;
    resourceType: typeof ResourceType.ENTITY;
    resourceId: string;
    resourceName: string;
    oldValue: EntityViewersForUserEvent;
    newValue: EntityViewersForUserEvent;
    source: EventSource | null;
}

interface ControlAdminsChangedEntityUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CONTROL_ADMINS_CHANGED;
    resourceType: typeof ResourceType.ENTITY;
    resourceId: string;
    resourceName: string;
    oldValue: ControlAccessRightsForAdminUsers;
    newValue: ControlAccessRightsForAdminUsers;
    source: EventSource | null;
}

interface ControlEditorsChangedEntityUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CONTROL_EDITORS_CHANGED;
    resourceType: typeof ResourceType.ENTITY;
    resourceId: string;
    resourceName: string;
    oldValue: ControlAccessRightsForEditUsers;
    newValue: ControlAccessRightsForEditUsers;
    source: EventSource | null;
}

interface ControlViewersChangedEntityUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CONTROL_VIEWERS_CHANGED;
    resourceType: typeof ResourceType.ENTITY;
    resourceId: string;
    resourceName: string;
    oldValue: ControlAccessRightsForViewUsers;
    newValue: ControlAccessRightsForViewUsers;
    source: EventSource | null;
}

interface RiskAdminsChangedEntityUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.RISK_ADMINS_CHANGED;
    resourceType: typeof ResourceType.ENTITY;
    resourceId: string;
    resourceName: string;
    oldValue: RiskAccessRightsForAdminUsers;
    newValue: RiskAccessRightsForAdminUsers;
    source: EventSource | null;
}

interface RiskEditorsChangedEntityUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.RISK_EDITORS_CHANGED;
    resourceType: typeof ResourceType.ENTITY;
    resourceId: string;
    resourceName: string;
    oldValue: RiskAccessRightsForEditUsers;
    newValue: RiskAccessRightsForEditUsers;
    source: EventSource | null;
}

interface RiskViewersChangedEntityUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.RISK_VIEWERS_CHANGED;
    resourceType: typeof ResourceType.ENTITY;
    resourceId: string;
    resourceName: string;
    oldValue: RiskAccessRightsForViewUsers;
    newValue: RiskAccessRightsForViewUsers;
    source: EventSource | null;
}

interface TagsChangedEntityUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.TAGS_CHANGED;
    resourceType: typeof ResourceType.ENTITY;
    resourceId: string;
    resourceName: string;
    oldValue: EntityTagsForUserEvent;
    newValue: EntityTagsForUserEvent;
    source: EventSource | null;
}

interface CreatedApiKeyUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CREATED;
    resourceType: typeof ResourceType.API_KEY;
    resourceId: string;
    resourceName: string;
    oldValue: ApiKeyForUserEvent;
    newValue: ApiKeyForUserEvent;
    source: EventSource | null;
}

interface DeletedApiKeyUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.DELETED;
    resourceType: typeof ResourceType.API_KEY;
    resourceId: string;
    resourceName: string;
    oldValue: ApiKeyForUserEvent;
    newValue: ApiKeyForUserEvent;
    source: EventSource | null;
}

interface TestingProgramForUserEvent {
    id: number;
    name: string;
    description: string;
    controlConclusionFieldGroup: { fields: Array<unknown> };
    sampleConclusionFieldGroup: { fields: Array<unknown> };
    created: string;
    createdBy: SimpleUserForUserEvent;
    usersWithAdminAccess: SimpleUserForUserEvent[];
    usersWithEditAccess: SimpleUserForUserEvent[];
    usersWithViewAccess: SimpleUserForUserEvent[];
}
interface CreatedTestingProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CREATED;
    resourceType: typeof ResourceType.TESTING_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: TestingProgramForUserEvent;
    newValue: TestingProgramForUserEvent;
    source: EventSource | null;
}

interface ChangedTestingProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CHANGED;
    resourceType: typeof ResourceType.TESTING_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: TestingProgramForUserEvent;
    newValue: TestingProgramForUserEvent;
    source: EventSource | null;
}

interface NameChangedTestingProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.NAME_CHANGED;
    resourceType: typeof ResourceType.TESTING_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: TestingProgramForUserEvent;
    newValue: TestingProgramForUserEvent;
    source: EventSource | null;
}

interface DescriptionChangedTestingProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.DESCRIPTION_CHANGED;
    resourceType: typeof ResourceType.TESTING_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: TestingProgramForUserEvent;
    newValue: TestingProgramForUserEvent;
    source: EventSource | null;
}

interface TasksChangedTestingProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.TASKS_CHANGED;
    resourceType: typeof ResourceType.TESTING_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: TestingProgramForUserEvent;
    newValue: TestingProgramForUserEvent;
    source: EventSource | null;
}

interface SampleTasksChangedTestingProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.SAMPLE_TASKS_CHANGED;
    resourceType: typeof ResourceType.TESTING_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: TestingProgramForUserEvent;
    newValue: TestingProgramForUserEvent;
    source: EventSource | null;
}

interface AdminsChangedTestingProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.ADMINS_CHANGED;
    resourceType: typeof ResourceType.TESTING_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: TestingProgramAdminsForUserEvent;
    newValue: TestingProgramAdminsForUserEvent;
    source: EventSource | null;
}

interface EditorsChangedTestingProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.EDITORS_CHANGED;
    resourceType: typeof ResourceType.TESTING_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: TestingProgramEditorsForUserEvent;
    newValue: TestingProgramEditorsForUserEvent;
    source: EventSource | null;
}

interface ViewersChangedTestingProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.VIEWERS_CHANGED;
    resourceType: typeof ResourceType.TESTING_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: TestingProgramViewersForUserEvent;
    newValue: TestingProgramViewersForUserEvent;
    source: EventSource | null;
}

interface DeletedTestingProgramUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.DELETED;
    resourceType: typeof ResourceType.TESTING_PROGRAM;
    resourceId: string;
    resourceName: string;
    oldValue: TestingProgramForUserEvent;
    newValue: TestingProgramForUserEvent;
    source: EventSource | null;
}

interface TestingControlForUpdateEvent {
    id: number;
    title: string;
    description: string;
    scheduling: {
        dueDate: string;
        dueTime: string;
        notificationTime: string;
        timezoneName: string;
    };
    samplingCriteria: {
        samplingTags: { category: string; name: string }[];
        periodStartMonth: string;
        periodStartYear: number;
        periodEndMonth: string;
        periodEndYear: number;
    };
    tags: { category: string; name: string }[];
    assignment: {
        responsible: {
            user: SimpleUserForUserEvent;
            allocatedTime: {
                days: number;
            };
        };
        reviewer?: {
            user: SimpleUserForUserEvent;
            allocatedTime: {
                days: number;
                hours: number;
            };
        };
        secondReviewer?: {
            user: SimpleUserForUserEvent;
            allocatedTime: {
                days: number;
                hours: number;
            };
        };
    };
    randomSampleCount: number;
}
interface CreatedTestingControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CREATED;
    resourceType: typeof ResourceType.TESTING_CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: TestingControlForUpdateEvent;
    newValue: TestingControlForUpdateEvent;
    source: EventSource | null;
}

interface ChangedTestingControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CHANGED;
    resourceType: typeof ResourceType.TESTING_CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: TestingControlForUpdateEvent;
    newValue: TestingControlForUpdateEvent;
    source: EventSource | null;
}

interface TitleChangedTestingControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.TITLE_CHANGED;
    resourceType: typeof ResourceType.TESTING_CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: TestingControlForUpdateEvent;
    newValue: TestingControlForUpdateEvent;
    source: EventSource | null;
}

interface DescriptionChangedTestingControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.DESCRIPTION_CHANGED;
    resourceType: typeof ResourceType.TESTING_CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: TestingControlForUpdateEvent;
    newValue: TestingControlForUpdateEvent;
    source: EventSource | null;
}

interface TagsChangedTestingControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.TAGS_CHANGED;
    resourceType: typeof ResourceType.TESTING_CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: TestingControlForUpdateEvent;
    newValue: TestingControlForUpdateEvent;
    source: EventSource | null;
}

interface SamplingCriteriaChangedTestingControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.SAMPLING_CRITERIA_CHANGED;
    resourceType: typeof ResourceType.TESTING_CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: TestingControlForUpdateEvent;
    newValue: TestingControlForUpdateEvent;
    source: EventSource | null;
}

interface SchedulingChangedTestingControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.SCHEDULING_CHANGED;
    resourceType: typeof ResourceType.TESTING_CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: TestingControlForUpdateEvent;
    newValue: TestingControlForUpdateEvent;
    source: EventSource | null;
}

interface AssigneesChangedTestingControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.ASSIGNEES_CHANGED;
    resourceType: typeof ResourceType.TESTING_CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: TestingControlForUpdateEvent;
    newValue: TestingControlForUpdateEvent;
    source: EventSource | null;
}

interface RecurrenceChangedTestingControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.RECURRENCE_CHANGED;
    resourceType: typeof ResourceType.TESTING_CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: TestingControlForUpdateEvent;
    newValue: TestingControlForUpdateEvent;
    source: EventSource | null;
}

interface DeletedTestingControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.DELETED;
    resourceType: typeof ResourceType.TESTING_CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: TestingControlForUpdateEvent;
    newValue: TestingControlForUpdateEvent;
    source: EventSource | null;
}

interface RandomSampleCountChangedTestingControlUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.RANDOM_SAMPLE_COUNT_CHANGED;
    resourceType: typeof ResourceType.TESTING_CONTROL;
    resourceId: string;
    resourceName: string;
    oldValue: TestingControlForUpdateEvent;
    newValue: TestingControlForUpdateEvent;
    source: EventSource | null;
}

interface ScimUserForEvent {
    externalId: string;
    userName: string;
    active: boolean;
}
interface CreatedScimUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CREATED;
    resourceType: typeof ResourceType.SCIM_USER;
    resourceId: string;
    resourceName: string;
    oldValue: ScimUserForEvent;
    newValue: ScimUserForEvent;
    source: EventSource | null;
}

interface ChangedScimUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CHANGED;
    resourceType: typeof ResourceType.SCIM_USER;
    resourceId: string;
    resourceName: string;
    oldValue: ScimUserForEvent;
    newValue: ScimUserForEvent;
    source: EventSource | null;
}

interface DeletedScimUserUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.DELETED;
    resourceType: typeof ResourceType.SCIM_USER;
    resourceId: string;
    resourceName: string;
    oldValue: ScimUserForEvent;
    newValue: ScimUserForEvent;
    source: EventSource | null;
}

interface ControlPolicyChangedOrganisationSettingsUserEvent {
    id: number;
    userId: number;
    created: string;
    name: typeof EventName.CONTROL_POLICY_CHANGED;
    resourceType: typeof ResourceType.ORGANISATION_SETTINGS;
    resourceId: string;
    resourceName: string;
    oldValue: ControlPolicyForUserEvent;
    newValue: ControlPolicyForUserEvent;
    source: EventSource | null;
}

type UserUserEvent =
    | CreatedUserUserEvent
    | CultureChangedUserUserEvent
    | DisabledUserUserEvent
    | EmailChangedUserUserEvent
    | EmailSettingsChangedUserUserEvent
    | NotificationPreferenceChangedUserUserEvent
    | EnabledUserUserEvent
    | FirstNameChangedUserUserEvent
    | InvitedUserUserEvent
    | LastNameChangedUserUserEvent
    | LoggedInUserUserEvent
    | LoggedOutUserUserEvent
    | PasswordAuthenticationChangedUserUserEvent
    | PasswordChangedUserUserEvent
    | PasswordResetUserUserEvent
    | PasswordResetRequestedUserUserEvent
    | PhoneNumberChangedUserUserEvent
    | PhoneNumberVerifiedUserUserEvent
    | RegisteredUserUserEvent
    | RolesChangedUserUserEvent
    | TimezoneChangedUserUserEvent;

type UserGroupUserEvent =
    | UsersChangedUserGroupUserEvent
    | DeletedUserGroupUserEvent
    | CreatedUserGroupUserEvent
    | NameChangedUserGroupUserEvent
    | DescriptionChangedUserGroupUserEvent
    | EditorsChangedUserGroupUserEvent
    | AdminsChangedUserGroupUserEvent;

type ControlUserEvent =
    | CreatedControlUserEvent
    | ControlProgramChangedControlUserEvent
    | TitleChangedControlUserEvent
    | DescriptionChangedControlUserEvent
    | TagsChangedControlUserEvent
    | RecurrenceChangedControlUserEvent
    | AssigneesChangedControlUserEvent
    | LoginProtectionChangedControlUserEvent
    | SegregationOfDutiesChangedControlUserEvent
    | TasksChangedControlUserEvent
    | DeletedControlUserEvent
    | NotificationRecipientsChangedControlUserEvent;

type ControlProgramUserEvent =
    | CreatedControlProgramUserEvent
    | NameChangedControlProgramUserEvent
    | DescriptionChangedControlProgramUserEvent
    | StatusChangedControlProgramUserEvent
    | TagsChangedControlProgramUserEvent
    | ResponsibleChangedControlProgramUserEvent
    | AdminsChangedControlProgramUserEvent
    | EditorsChangedControlProgramUserEvent
    | ViewersChangedControlProgramUserEvent
    | EntitiesChangedControlProgramUserEvent
    | DeletedControlProgramUserEvent;

type RiskDirectoryUserEvent =
    | AdminsChangedRiskDirectoryUserEvent
    | EditorsChangedRiskDirectoryUserEvent
    | ViewersChangedRiskDirectoryUserEvent;

type EntityUserEvent =
    | CreatedEntityUserEvent
    | DeletedEntityUserEvent
    | NameChangedEntityUserEvent
    | DescriptionChangedEntityUserEvent
    | ResponsibleChangedEntityUserEvent
    | AdminsChangedEntityUserEvent
    | EditorsChangedEntityUserEvent
    | ViewersChangedEntityUserEvent
    | ControlAdminsChangedEntityUserEvent
    | ControlEditorsChangedEntityUserEvent
    | ControlViewersChangedEntityUserEvent
    | RiskAdminsChangedEntityUserEvent
    | RiskEditorsChangedEntityUserEvent
    | RiskViewersChangedEntityUserEvent
    | TagsChangedEntityUserEvent;

type ApiKeyUserEvent = CreatedApiKeyUserEvent | DeletedApiKeyUserEvent;

type TestingProgramUserEvent =
    | CreatedTestingProgramUserEvent
    | ChangedTestingProgramUserEvent
    | NameChangedTestingProgramUserEvent
    | DescriptionChangedTestingProgramUserEvent
    | TasksChangedTestingProgramUserEvent
    | SampleTasksChangedTestingProgramUserEvent
    | AdminsChangedTestingProgramUserEvent
    | EditorsChangedTestingProgramUserEvent
    | ViewersChangedTestingProgramUserEvent
    | DeletedTestingProgramUserEvent;

type TestingControlUserEvent =
    | CreatedTestingControlUserEvent
    | ChangedTestingControlUserEvent
    | TitleChangedTestingControlUserEvent
    | DescriptionChangedTestingControlUserEvent
    | TagsChangedTestingControlUserEvent
    | SamplingCriteriaChangedTestingControlUserEvent
    | SchedulingChangedTestingControlUserEvent
    | AssigneesChangedTestingControlUserEvent
    | RecurrenceChangedTestingControlUserEvent
    | DeletedTestingControlUserEvent
    | RandomSampleCountChangedTestingControlUserEvent;

type ScimUserUserEvent =
    | CreatedScimUserUserEvent
    | ChangedScimUserUserEvent
    | DeletedScimUserUserEvent;

type OrganisationSettingsUserEvent =
    ControlPolicyChangedOrganisationSettingsUserEvent;

type UserEvent =
    | UserUserEvent
    | UserGroupUserEvent
    | ControlUserEvent
    | ControlProgramUserEvent
    | RiskDirectoryUserEvent
    | EntityUserEvent
    | ApiKeyUserEvent
    | TestingProgramUserEvent
    | TestingControlUserEvent
    | ScimUserUserEvent
    | OrganisationSettingsUserEvent;

export type {
    EventWithRelations,
    UserUserEvent,
    UserGroupUserEvent,
    ControlUserEvent,
    RiskDirectoryUserEvent,
    EntityUserEvent,
    ApiKeyUserEvent,
    UserEvent,
    ControlProgramUserEvent,
    RolesChangedUserUserEvent,
    CreatedUserUserEvent,
    DisabledUserUserEvent,
    EnabledUserUserEvent,
    UserForUserEvent,
    TestingProgramUserEvent,
    TestingControlUserEvent,
    ScimUserUserEvent,
    OrganisationSettingsUserEvent,
};
export { ResourceType, RESOURCE_TYPES, EventName };
